import {
  Action,
  Button,
  Card,
  closeDialog,
  DataStatus,
  openDialog,
  Parameter,
  Separator,
  showNotification,
  Wizard,
  WizardStepConfig,
} from 'platform/components';
import {Box, Heading, HStack, Scroll, Text, VStack} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';
import {match} from 'ts-pattern';

import {useCallback, useState} from 'react';

import {isNil} from 'ramda';
import {isNilOrEmpty, isNotNil, isNotNilOrEmpty, isString} from 'ramda-adjunct';

import {useGetManufacturersQuery} from '@dms/api/core';
import {
  AlternativeSparePartArticle,
  useGetAlternativeSparePartsByCatalogueNumberQuery,
} from '@dms/api/warehouse';
import {
  useGetOrderItemForMaterialQuery,
  usePostServiceOrderItemReplaceWithAlternativeMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {warehouseRoutes} from '@dms/routes';
import {DmsDataGrid, FullScreenModal, handleApiError, refreshDmsDatagrid} from '@dms/shared';
import {ArticleCreate} from '@dms/shared-aftersales';

import {buildArray, composePath, Nullish, suffixTestId, TestIdProps, useNavigate} from 'shared/web';

import type {ActionCallback, QueryFilterObject, RowData} from 'features/eag-grid';

const ALTERNATIVES_GRID_CODE = 'service-service-order-alternative-parts';

// 'name' is predefined so the pre-fill survives the user hiding the column; on grid
// configurations where the column does not exist at all it is dropped from the data
// query (see getVisibleColumnKeys) and the callback reads it optionally.
const PREDEFINED_COLUMNS = ['catalogNumber', 'manufacturer', 'name'] as const;

// The "Alternatives" step is the first step of the wizard.
const ALTERNATIVES_STEP_INDEX = 0;

const CREATE_ARTICLE_DIALOG = 'findAlternativeCreateArticleDialog';

// Column on the alternatives grid that holds the catalogue number used to look up
// warehouse articles in the warehouse step.
const getRowCatalogueNumber = (row: RowData): string | undefined => {
  const catalogNumber = row.catalogNumber as {value?: unknown} | undefined;
  return isString(catalogNumber?.value) ? catalogNumber.value : undefined;
};

// The manufacturer column is a relation cell: it usually exposes the manufacturer as an
// entity ({id, label}), but some grids expose only the name string. Normalise both shapes so
// callers can read the id directly and fall back to a name lookup when no id is present.
const parseManufacturerCellValue = (value: unknown): {id?: string; name?: string} => {
  if (isString(value)) {
    return {name: value};
  }

  if (isNotNil(value) && typeof value === 'object') {
    const entity = value as {id?: unknown; label?: unknown};

    return {
      id: isString(entity.id) ? entity.id : undefined,
      name: isString(entity.label) ? entity.label : undefined,
    };
  }

  return {};
};

const getRowManufacturer = (row: RowData) =>
  parseManufacturerCellValue((row.manufacturer as {value?: unknown} | undefined)?.value);

const getArticleTitle = (article: AlternativeSparePartArticle, catalogueNumber: string) =>
  buildArray<string>()
    .add(catalogueNumber)
    .when(article.name, article.name)
    .whenNotNil(article.manufacturer?.name)
    .whenNotNil(article.warehouse?.name)
    .join(' • ');

const openArticleDetail = (article: AlternativeSparePartArticle) => {
  if (!article.warehouse?.id) {
    return;
  }

  window.open(
    composePath(warehouseRoutes.articleDetailOverview, {
      params: {warehouseId: article.warehouse.id, id: article.warehouseArticleId},
    }),
    '_blank'
  );
};

interface FindAlternativeDialogProps extends TestIdProps {
  serviceCaseId: string;
  serviceOrderId: string;
  serviceJobId: string;
  serviceOrderJobItemId: string;
  authorizationProfileId: string;
  itemName?: string | null;
  onClose: () => void;
  onReplaced?: () => void;
}

// eslint-disable-next-line max-lines-per-function -- Pre-existing violation
export function FindAlternativeDialog(props: FindAlternativeDialogProps) {
  const formatCurrency = useFormatCurrency();
  const navigate = useNavigate();

  const [selectedAlternativeRows, setSelectedAlternativeRows] = useState<RowData[]>([]);
  const [selectedWarehouseArticleId, setSelectedWarehouseArticleId] = useState<string | null>(null);

  const {data: manufacturers} = useGetManufacturersQuery();

  // Search payload for the warehouse step: each selected alternative as
  // {catalogueNumber, manufacturerId}. The grid only carries the manufacturer name, so resolve
  // it to an id via the manufacturer list. Rows whose catalogue number or manufacturer can't
  // be resolved are dropped (which also keeps the query skipped until manufacturers load).
  const selectedAlternatives = selectedAlternativeRows
    .map((row) => {
      const catalogueNumber = getRowCatalogueNumber(row);
      const {id: manufacturerEntityId, name: manufacturerName} = getRowManufacturer(row);
      const manufacturerId =
        manufacturerEntityId ??
        (isString(manufacturerName)
          ? manufacturers?.find((manufacturer) => manufacturer.name === manufacturerName)
              ?.manufacturerId
          : undefined);

      return isNotNil(catalogueNumber) && isNotNil(manufacturerId)
        ? {catalogueNumber, manufacturerId}
        : null;
    })
    .filter(isNotNil);

  // Catalogue numbers of the selected alternatives that still offer the "create article"
  // primary action — i.e. they have no warehouse article yet and block continuing to the
  // warehouse step. Surfaced in the disabled-Continue tooltip so the user knows which ones.
  const catalogueNumbersWithoutArticle = selectedAlternativeRows
    .filter((row) => row.actions?.primary?.includes('createArticle'))
    .map(getRowCatalogueNumber)
    .filter(isNotNil);

  // The warehouse step can only be reached when at least one alternative is selected and all
  // selected alternatives already have a warehouse article to pick from.
  const canContinueToWarehouse =
    isNotNilOrEmpty(selectedAlternativeRows) && isNilOrEmpty(catalogueNumbersWithoutArticle);

  const {
    data: materialItem,
    isLoading: isMaterialItemLoading,
    isError: isMaterialItemError,
  } = useGetOrderItemForMaterialQuery({
    serviceCaseId: props.serviceCaseId,
    serviceOrderId: props.serviceOrderId,
    serviceJobId: props.serviceJobId,
    serviceItemId: props.serviceOrderJobItemId,
  });

  const {
    data: alternativeGroups,
    isFetching: isAlternativesFetching,
    isError: isAlternativesError,
    refetch: refetchAlternatives,
  } = useGetAlternativeSparePartsByCatalogueNumberQuery(
    {body: selectedAlternatives},
    {skip: isNilOrEmpty(selectedAlternatives)}
  );

  const [replaceWithAlternative, {isLoading: isReplacing}] =
    usePostServiceOrderItemReplaceWithAlternativeMutation();

  const catalogNumber = materialItem?.number;

  const queryModifier = useCallback(
    (filter: QueryFilterObject) => ({
      ...filter,
      catalogNumber: catalogNumber ?? '',
      authorizationProfileId: props.authorizationProfileId,
    }),
    [catalogNumber, props.authorizationProfileId]
  );

  const handleRowSelectionChange = (selectedRows: RowData[]) => {
    setSelectedAlternativeRows(selectedRows);
    // Reset the warehouse-article choice whenever the set of alternatives changes.
    setSelectedWarehouseArticleId(null);
  };

  const handleStepChange = (step: number) => {
    // Going back to the Alternatives step starts the flow over: the grid remounts with an
    // empty selection, so clear the parent state to keep it in sync.
    if (step === ALTERNATIVES_STEP_INDEX) {
      setSelectedAlternativeRows([]);
      setSelectedWarehouseArticleId(null);
    }
  };

  const handleReplace = useCallback(() => {
    if (!selectedWarehouseArticleId) {
      return;
    }

    replaceWithAlternative({
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.serviceOrderId,
      serviceJobId: props.serviceJobId,
      serviceItemId: props.serviceOrderJobItemId,
      body: {warehouseArticleId: selectedWarehouseArticleId},
    })
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.changesSaved'));
        props.onReplaced?.();
        props.onClose();
      })
      .catch(handleApiError);
  }, [selectedWarehouseArticleId, replaceWithAlternative, props]);

  const getArticleParameters = useCallback(
    (article: AlternativeSparePartArticle) => {
      const formatMoney = (money: {amount?: number | null; currency?: string | null} | Nullish) => {
        if (isNil(money) || isNil(money.amount) || isNil(money.currency)) {
          return null;
        }

        return formatCurrency(money.amount, money.currency);
      };

      const formatQuantity = (quantity: {amount: number; unit: string} | Nullish) =>
        isNotNil(quantity) ? `${quantity.amount} ${quantity.unit}` : null;

      const purchasePrice = formatMoney(article.purchasePrice);
      const sellingPrice = formatMoney(article.sellingPrice);
      const margin = formatMoney(article.margin);
      const available = formatQuantity(article.availableQuantity);
      const inTransitForRequest = formatQuantity(article.inTransitForRequest);
      const inTransitFree = formatQuantity(article.inTransitFree);

      return buildArray<Parameter>()
        .when(purchasePrice, `${i18n.t('entity.warehouse.labels.purchase')}: ${purchasePrice}`)
        .when(sellingPrice, `${i18n.t('entity.warehouse.labels.selling')}: ${sellingPrice}`)
        .when(margin, `${i18n.t('entity.warehouse.labels.margin')}: ${margin}`)
        .when(available, `${i18n.t('entity.warehouse.labels.available')}: ${available}`)
        .when(
          isNotNil(inTransitForRequest) || isNotNil(inTransitFree),
          `${i18n.t('entity.warehouse.labels.inTransit')}: ${inTransitForRequest ?? '0'} / ${inTransitFree ?? '0'}`
        )
        .when(
          article.measurementUnit,
          `${i18n.t('entity.warehouse.labels.measurementUnit')}: ${article.measurementUnit}`
        )
        .when(
          isNotNil(article.dispensingUnit),
          `${i18n.t('entity.warehouse.labels.dispensingUnit')}: ${article.dispensingUnit}`
        )
        .when(
          article.defaultSupplier?.name,
          `${i18n.t('entity.warehouse.labels.defaultSupplier')}: ${article.defaultSupplier?.name}`
        );
    },
    [formatCurrency]
  );

  const handleCreateNewArticle = useCallback(
    (
      defaultValues: {catalogNumber: string; manufacturerId?: string; name?: string},
      onCreated?: () => void
    ) => {
      openDialog(
        <ArticleCreate
          defaultValues={defaultValues}
          submitButtonTitle={i18n.t('general.actions.create')}
          hasCreateAndEditButton
          onArticleCreate={(createdArticleData) => {
            if (createdArticleData.submittedButton === 'CREATE_AND_EDIT') {
              navigate(
                composePath(warehouseRoutes.articleDetailOverview, {
                  params: {
                    warehouseId: createdArticleData.formValues.warehouseId,
                    id: createdArticleData.articleId,
                  },
                })
              );
              return;
            }

            closeDialog(CREATE_ARTICLE_DIALOG);
            onCreated?.();
          }}
          onClose={() => closeDialog(CREATE_ARTICLE_DIALOG)}
          data-testid={suffixTestId('createArticleDialog', props)}
        />,
        {
          id: CREATE_ARTICLE_DIALOG,
          title: i18n.t('entity.warehouse.actions.createNewArticle'),
          scrollBehavior: 'inside',
          withAdditionalFooter: true,
          size: 'large',
        }
      );
    },
    [navigate, props]
  );

  const handleAlternativesGridAction = useCallback<ActionCallback<typeof PREDEFINED_COLUMNS>>(
    ({actionKey, getColumnData}) =>
      match(actionKey)
        .with('createArticle', () => {
          const catalogueNumber = getColumnData<string>('catalogNumber')?.value;

          if (!isString(catalogueNumber)) {
            return;
          }

          // Read the manufacturer id from the relation cell, falling back to resolving it from
          // the manufacturer name via the manufacturer list, before pre-filling the form.
          const {id: manufacturerEntityId, name: manufacturerName} = parseManufacturerCellValue(
            getColumnData('manufacturer')?.value
          );
          const manufacturerId =
            manufacturerEntityId ??
            (isString(manufacturerName)
              ? manufacturers?.find((manufacturer) => manufacturer.name === manufacturerName)
                  ?.manufacturerId
              : undefined);

          // The name column may not exist on every grid configuration, so read it optionally
          // to pre-fill the new article without erroring when it is absent.
          const name = getColumnData<string>('name', {isOptional: true})?.value;

          handleCreateNewArticle({catalogNumber: catalogueNumber, manufacturerId, name}, () => {
            refreshDmsDatagrid(ALTERNATIVES_GRID_CODE);
            setSelectedAlternativeRows([]);
          });
        })
        .otherwise(() => undefined),
    [handleCreateNewArticle, manufacturers]
  );

  const closeAction: Action = {
    type: 'button',
    variant: 'secondary',
    title: i18n.t('general.actions.close'),
    onClick: props.onClose,
    'data-testid': suffixTestId('close', props),
  };

  const steps = buildArray<WizardStepConfig>()
    .add({
      title: i18n.t('entity.findAlternative.steps.alternatives.title'),
      subtitle: i18n.t('entity.findAlternative.steps.alternatives.subtitle'),
      // While the warehouse step can't be reached the Wizard renders no "Continue" button
      // and would hide the footer. Provide our own disabled Continue wrapped in a tooltip
      // to keep the footer visible and explain why it is disabled. The reason differs: either
      // nothing is selected yet, or a selected alternative has no warehouse article to pick
      // from. Once the selection allows continuing the Wizard shows its own enabled Continue.
      actions: !canContinueToWarehouse
        ? [
            {
              type: 'buttonWithTooltip',
              variant: 'secondary',
              title: i18n.t('general.actions.continue'),
              isDisabled: true,
              tooltip: {
                description: isNilOrEmpty(selectedAlternativeRows)
                  ? i18n.t('entity.findAlternative.steps.alternatives.selectAtLeastOneTooltip')
                  : i18n.t(
                      'entity.findAlternative.steps.alternatives.articleNotInWarehouseTooltip',
                      {catalogueNumbers: catalogueNumbersWithoutArticle.join(', ')}
                    ),
              },
              'data-testid': suffixTestId('continueButton', props),
            },
          ]
        : undefined,
      content: (
        // paddingHorizontal aligns the content with the wizard footer, whose actions are
        // inset by the Wizard's own paddingHorizontal in horizontal orientation.
        <Box height="100%" paddingHorizontal={6}>
          <VStack spacing={4} height="100%">
            <VStack spacing={1}>
              <Heading size={4}>
                {i18n.t('entity.findAlternative.steps.alternatives.title')}
              </Heading>
              <Text size="small" color="tertiary">
                {i18n.t('entity.findAlternative.steps.alternatives.description')}
              </Text>
            </VStack>
            {/* Cap the grid height so it never grows down to the viewport bottom: a
                non-autoHeight grid otherwise fills all space below its top edge, pushing the
                wizard's Continue/Close footer off-screen. The reserved amount covers the modal
                chrome (header, step bar, heading, footer, paddings) so the footer stays visible
                without scrolling while keeping the gap below the grid small. */}
            <Box flex={1} minHeight={0} maxHeight="calc(100vh - 18rem)">
              <DataStatus isLoading={isMaterialItemLoading} isError={isMaterialItemError}>
                <DmsDataGrid
                  gridCode={ALTERNATIVES_GRID_CODE}
                  queryModifier={queryModifier}
                  actionCallback={handleAlternativesGridAction}
                  predefinedColumns={PREDEFINED_COLUMNS}
                  onRowSelectionChange={handleRowSelectionChange}
                  data-testid={suffixTestId('alternativesDataGrid', props)}
                />
              </DataStatus>
            </Box>
          </VStack>
        </Box>
      ),
    })
    .add({
      title: i18n.t('entity.warehouse.labels.warehouse'),
      subtitle: i18n.t('entity.findAlternative.steps.warehouse.subtitle'),
      isDisabled: !canContinueToWarehouse,
      actions: [
        {
          type: 'button',
          variant: 'primary',
          title: i18n.t('general.actions.replace'),
          isLoading: isReplacing,
          isDisabled: !selectedWarehouseArticleId,
          onClick: handleReplace,
          'data-testid': suffixTestId('replace', props),
        },
      ],
      content: (
        // paddingHorizontal aligns the content with the wizard footer, whose actions are
        // inset by the Wizard's own paddingHorizontal in horizontal orientation.
        <Box height="100%" paddingHorizontal={6}>
          <VStack spacing={4} height="100%">
            <VStack spacing={1}>
              <Heading size={4}>{i18n.t('entity.warehouse.labels.warehouse')}</Heading>
              <Text size="small" color="tertiary">
                {i18n.t('entity.findAlternative.steps.warehouse.description')}
              </Text>
            </VStack>
            <Box flex={1} minHeight={0}>
              <DataStatus
                isLoading={isAlternativesFetching}
                isError={isAlternativesError}
                isEmpty={isNilOrEmpty(alternativeGroups)}
              >
                <Scroll auto>
                  <VStack spacing={4}>
                    {alternativeGroups?.map((group) => (
                      <VStack key={group.catalogueNumber} spacing={4}>
                        <Separator spacing={0} />
                        <HStack align="center" justify="space-between">
                          <Text size="base" alternative>
                            {i18n.t('entity.findAlternative.labels.catalogueNumber', {
                              catalogueNumber: group.catalogueNumber,
                            })}
                          </Text>
                          <Button
                            variant="link"
                            size="small"
                            leftIcon="content/add_circle"
                            title={i18n.t('entity.warehouse.actions.createNewArticle')}
                            onClick={() => {
                              // Every group on the warehouse step has at least one article
                              // (reaching this step requires it). Reuse the manufacturer used to
                              // search for this catalogue number — falling back to the article's
                              // manufacturer — and the article's name to pre-fill the new article.
                              const article = group.articles?.find(isNotNil);
                              const manufacturerId =
                                selectedAlternatives.find(
                                  (alternative) =>
                                    alternative.catalogueNumber === group.catalogueNumber
                                )?.manufacturerId ??
                                article?.manufacturer?.id ??
                                undefined;

                              handleCreateNewArticle(
                                {
                                  catalogNumber: group.catalogueNumber,
                                  manufacturerId,
                                  name: article?.name,
                                },
                                refetchAlternatives
                              );
                            }}
                            data-testid={suffixTestId(
                              `createArticle-${group.catalogueNumber}`,
                              props
                            )}
                          />
                        </HStack>
                        <VStack spacing={4}>
                          {group.articles?.filter(isNotNil).map((article) => (
                            <Card
                              key={article.warehouseArticleId}
                              variant="inlineWhite"
                              title={getArticleTitle(article, group.catalogueNumber)}
                              parameters={getArticleParameters(article)}
                              control={{
                                type: 'radio',
                                value: selectedWarehouseArticleId === article.warehouseArticleId,
                                onChange: () =>
                                  setSelectedWarehouseArticleId(article.warehouseArticleId),
                              }}
                              actions={buildArray<Action>().when(article.warehouse?.id, {
                                type: 'button',
                                variant: 'secondary',
                                size: 'small',
                                rightIcon: 'action/open_in_new',
                                title: i18n.t('entity.warehouse.labels.articleDetail'),
                                onClick: () => openArticleDetail(article),
                                'data-testid': suffixTestId(
                                  `articleDetail-${article.warehouseArticleId}`,
                                  props
                                ),
                              })}
                            />
                          ))}
                        </VStack>
                      </VStack>
                    ))}
                  </VStack>
                </Scroll>
              </DataStatus>
            </Box>
          </VStack>
        </Box>
      ),
    });

  return (
    <FullScreenModal
      headline={i18n.t('entity.findAlternative.title')}
      onClose={props.onClose}
      // The horizontal Wizard paints no background of its own, so the modal body colour shows
      // through behind it. Override the default neutral grey with white for this dialog.
      backgroundColor="palettes.white.10.100"
      data-testid={suffixTestId('findAlternativeDialog', props)}
    >
      <Box height="100%" padding={4}>
        {/* Grid stretches the Wizard's (unstyled) wrapper div to full height so its
            internal `height="100%"` chain can resolve down to the step content. */}
        {/* eslint-disable-next-line react/forbid-dom-props -- Pre-existing violation */}
        <div style={{display: 'grid', gridTemplateRows: '1fr', height: '100%'}}>
          <Wizard
            orientation="horizontal"
            height="100%"
            steps={steps}
            actions={[closeAction]}
            onStepChange={handleStepChange}
            data-testid={suffixTestId('wizard', props)}
          />
        </div>
      </Box>
    </FullScreenModal>
  );
}
